import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { ICompany } from "@app/core/models/ICompany";
import { SentTypeEmail } from "@app/core/models/SendTypeEmail";
import { IDistributor } from "@app/distributor/models/IDistributor";
import { IProfile } from "@app/distributor/models/IProfiles";
import { Api } from "@app/shared/config/Api";
import { IInstrumentTechnician } from "@app/shared/models/appointments/iInstrumentTechnician.model";
import { IApiPaginatedData } from "@app/shared/models/iApiPaginatedData.model";
import { IApiResponse } from "@app/shared/models/iApiResponse.model";
import { ErrorService } from "@app/shared/services/error/error.service";
import {
  catchError, map, Subject, tap
} from "rxjs";

@Injectable({
  providedIn: "root",
})
export class CompanyService {
  private http = inject(HttpClient);
  private errorService = inject(ErrorService);
  api = new Api();

  refresh$ = new Subject<void>();

  get refreshNeeded() {
    return this.refresh$.next();
  }

  getCompanies() {
    return this.http.get<ICompany[]>(`${this.api.getHostV1()}/companies/`);
  }

  getProfiles() {
    return this.http.get<IProfile[]>(
      `${this.api.getHostV1()}/profiles/list-distributors/`
    );
  }

  getCompanyById(id: string) {
    return this.http.get<ICompany>(`${this.api.getHostV1()}/companies/${id}/`);
  }

  createCompany(company: FormData) {
    return this.http
      .post<IApiResponse>(`${this.api.getHostV1()}/companies/`, company)
      .pipe(
        catchError(this.errorService.handleError),
        tap(() => {
          this.refresh$.next();
        })
      );
  }

  updateCompany(company: FormData, id: string) {
    return this.http
      .put<IApiResponse>(`${this.api.getHostV1()}/companies/${id}/`, company)
      .pipe(
        catchError(this.errorService.handleError),
        tap(() => {
          this.refresh$.next();
        })
      );
  }

  importDistributorsUsersFile(file: File, id: string) {
    const formData = new FormData();
    formData.append("attachment", file);
    return this.http
      .post(`${this.api.getHostV1()}/companies/${id}/import-users/`, formData)
      .pipe(catchError(this.errorService.handleError));
  }

  importDistributorsByFile(file: File) {
    const formData = new FormData();
    formData.append("attachment", file);
    return this.http
      .post(`${this.api.getHostV1()}/companies/import/`, formData)
      .pipe(catchError(this.errorService.handleError));
  }

  createCompanyUser(user: FormData, id: string) {
    return this.http
      .post<IApiResponse>(
      `${this.api.getHostV1()}/companies/${id}/users/`,
      user
    )
      .pipe(catchError(this.errorService.handleError));
  }

  getTechniciansByCompany(companyId: string) {
    return this.http.get<IInstrumentTechnician[]>(
      `${this.api.getHostV1()}/companies/${companyId}/list-instrument-technicians/`
    );
  }

  getCompanyUsers(id: string) {
    return this.http
      .get<IDistributor[]>(
      `${this.api.getHostV1()}/companies/${id}/list-users/`
    )
      .pipe(
        map((distributors) => distributors
          .slice()
          .sort(
            (a, b) => new Date(b.createdAt!).getTime()
                - new Date(a.createdAt!).getTime()
          ))
      )
      .pipe(catchError(this.errorService.handleError));
  }

  deleteCompanyUser(id: string) {
    return this.http.delete<IDistributor>(
      `${this.api.getHostV1()}/distributors/${id}/`
    );
  }

  deleteCompany(id: string) {
    return this.http.delete<IDistributor>(
      `${this.api.getHostV1()}/companies/${id}/`
    );
  }

  resendEmail(email: string) {
    const data = new FormData();
    data.append("sendType", SentTypeEmail.SEND_CHANGE_PASSWORD);
    data.append("recipientEmail", email);
    return this.http
      .post<IApiResponse>(
      `${this.api.getHostV1()}/notifications/mail/resend/`,
      data
    )
      .pipe(catchError(this.errorService.handleError));
  }

  getFreelancers(companyId: string) {
    return this.http.get<IApiPaginatedData<IInstrumentTechnician[]>>(
      `${this.api.getHostV1()}/companies/${companyId}/list-freelancers/`
    );
  }

  getNextFreelancersPage(url: string) {
    return this.http
      .get<IApiPaginatedData<IInstrumentTechnician[]>>(url)
      .pipe(catchError(this.errorService.handleError));
  }

  attachFreelancer(companyId: string, freelancerId: string) {
    return this.http
      .post<IApiResponse>(
      `${this.api.getHostV1()}/companies/${companyId}/attach-freelancer/${freelancerId}/`,
      {}
    )
      .pipe(catchError(this.errorService.handleError));
  }

  searchFreelancer(companyId: string, cpf: string) {
    return this.http
      .get<IApiPaginatedData<IInstrumentTechnician[]>>(
      `${this.api.getHostV1()}/companies/${companyId}/list-freelancers/?identification_number=${cpf}`
    )
      .pipe(catchError(this.errorService.handleError));
  }
}
